"use client"

import { FC, useRef } from "react"
import { Content } from "@prismicio/client"
import { PrismicRichText, SliceComponentProps } from "@prismicio/react"
import {
  LazyMotion,
  domAnimation,
  m,
  type Variants,
  useScroll,
  useTransform,
  useSpring,
} from "motion/react"
import { vFadeUp, vFadeIn, vStaggerContainer } from "@/lib/animations"

export type QuoteWithDescriptionProps =
  SliceComponentProps<Content.QuoteWithDescriptionSlice>

const vLineExpand: Variants = {
  hidden: { scaleX: 0, opacity: 0 },
  visible: {
    scaleX: 1,
    opacity: 1,
    transition: { duration: 0.9, ease: "easeOut" },
  },
}

const QuoteWithDescription: FC<QuoteWithDescriptionProps> = ({ slice }) => {
  const { primary } = slice
  const sectionRef = useRef<HTMLElement>(null)

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  })

  // Background bloom drifts upward as you scroll through
  const bloomY = useTransform(scrollYProgress, [0, 1], ["15%", "-15%"])
  const smoothBloomY = useSpring(bloomY, { stiffness: 60, damping: 20 })

  // Quote text rises at a different speed to the page
  const textY = useTransform(scrollYProgress, [0, 1], [50, -70])
  const smoothTextY = useSpring(textY, { stiffness: 55, damping: 20 })

  // ✦ ornaments rotate as you scroll (top forward, bottom backward)
  const ornamentRotateFwd = useTransform(scrollYProgress, [0, 1], [0, 360])
  const ornamentRotateBwd = useTransform(scrollYProgress, [0, 1], [0, -360])

  // Twin vertical lines grow downward from the top
  const progressScaleY = useTransform(scrollYProgress, [0.05, 0.9], [0, 1])

  return (
    <LazyMotion features={domAnimation}>
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        ref={sectionRef}
        aria-label={slice.slice_type
          .split("_")
          .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ")}
        className="relative overflow-hidden section-py"
        style={{
          background:
            "linear-gradient(150deg, oklch(0.21 0.085 22) 0%, oklch(0.29 0.11 32) 45%, oklch(0.22 0.09 20) 100%)",
        }}
      >
        {/* Central radial bloom — parallax */}
        <m.div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 65% 55% at 50% 50%, oklch(0.38 0.13 35 / 0.45) 0%, transparent 70%)",
            y: smoothBloomY,
          }}
        />

        {/* Twin vertical scroll progress lines */}
        <m.div
          aria-hidden
          className="absolute inset-y-0 left-6 w-px origin-top rounded-full"
          style={{
            scaleY: progressScaleY,
            background:
              "linear-gradient(to bottom, var(--sacred-gold), oklch(0.59 0.17 42 / 0.3), transparent)",
          }}
        />
        <m.div
          aria-hidden
          className="absolute inset-y-0 right-6 w-px origin-top rounded-full"
          style={{
            scaleY: progressScaleY,
            background:
              "linear-gradient(to bottom, var(--sacred-gold), oklch(0.59 0.17 42 / 0.3), transparent)",
          }}
        />

        {/* Edge vignette corners */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 120% 120% at 0% 0%, oklch(0.15 0.06 20 / 0.5) 0%, transparent 50%), radial-gradient(ellipse 120% 120% at 100% 100%, oklch(0.15 0.06 20 / 0.5) 0%, transparent 50%)",
          }}
        />

        <m.div
          className="relative container flex flex-col items-center gap-8 text-center"
          variants={vStaggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
        >
          {/* Top ornament */}
          <m.div
            variants={vFadeIn}
            className="flex flex-col items-center gap-3"
          >
            <m.span
              className="text-xl leading-none"
              style={{
                color: "var(--sacred-gold)",
                display: "inline-block",
                rotate: ornamentRotateFwd,
              }}
            >
              ✦
            </m.span>
            <m.div
              variants={vLineExpand}
              className="h-px w-56 origin-center"
              style={{
                background:
                  "linear-gradient(to right, transparent, var(--sacred-gold), transparent)",
              }}
            />
          </m.div>

          {/* Quote text */}
          {primary.quote && (
            <m.div
              variants={vFadeUp}
              className="max-w-2xl"
              style={{ y: smoothTextY }}
            >
              <PrismicRichText
                field={primary.quote}
                components={{
                  paragraph: ({ children }) => (
                    <p
                      className="my-3 text-base/loose first:mt-0 last:mb-0 md:text-lg/loose"
                      style={{ color: "var(--sacred-cream)" }}
                    >
                      {children}
                    </p>
                  ),
                  strong: ({ children }) => (
                    <strong
                      className="font-bold"
                      style={{ color: "var(--sacred-gold)" }}
                    >
                      {children}
                    </strong>
                  ),
                  em: ({ children }) => (
                    <em style={{ color: "var(--sacred-gold)" }}>{children}</em>
                  ),
                  heading1: ({ children }) => (
                    <h1
                      className="mb-2"
                      style={{ color: "var(--sacred-gold)" }}
                    >
                      {children}
                    </h1>
                  ),
                  heading2: ({ children }) => (
                    <h2
                      className="mb-2"
                      style={{ color: "var(--sacred-gold)" }}
                    >
                      {children}
                    </h2>
                  ),
                }}
              />
            </m.div>
          )}

          {/* Bottom ornament */}
          <m.div
            variants={vFadeIn}
            className="flex flex-col items-center gap-3"
          >
            <m.div
              variants={vLineExpand}
              className="h-px w-56 origin-center"
              style={{
                background:
                  "linear-gradient(to right, transparent, var(--sacred-gold), transparent)",
              }}
            />
            <m.span
              className="text-xl leading-none"
              style={{
                color: "var(--sacred-gold)",
                display: "inline-block",
                rotate: ornamentRotateBwd,
              }}
            >
              ✦
            </m.span>
          </m.div>
        </m.div>
      </section>
    </LazyMotion>
  )
}

export default QuoteWithDescription
